import React from 'react'
import { Field, FieldProps, FieldRenderProps } from 'react-final-form'
import TextField from 'components/ui/TextField'
import NumberFormat, { NumberFormatValues } from 'react-number-format'
import { FieldValidator } from 'final-form'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const EMPTY_FIELD_PROPS: Partial<FieldProps<any, any>> = {}

type PureFormMoneyFieldProps = {
  label?: React.ReactElement | string
  description?: string | React.ReactElement
  inputRightAdornment?: React.ReactElement
  placeholder?: string
  currency: string
}

export type Props = PureFormMoneyFieldProps & {
  name: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldProps?: Partial<FieldProps<any, any>>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validate?: FieldValidator<any>
  newDesign?: boolean
}

type MoneyFieldProps = {
  name: string
  label?: React.ReactElement | string
  error?: boolean | null
  description?: string | React.ReactElement
  inputRightAdornment?: React.ReactElement
  placeholder?: string
  currency: string
  value: number | null | string
  onChange: (event: { target: NumberFormatValues }) => void
  newDesign?: boolean
}

export function MoneyField({
  value,
  onChange,
  error,
  label,
  description,
  placeholder,
  currency,
  name,
  ...other
}: MoneyFieldProps) {
  return (
    <NumberFormat
      {...other}
      customInput={TextField}
      decimalScale={2}
      description={description}
      error={error}
      inputAdornment={currency}
      label={label}
      name={name}
      onValueChange={(target) => onChange({ target })}
      placeholder={placeholder}
      type="text"
      value={value}
      isNumericString
    />
  )
}

function PureFormMoneyField({
  input,
  label,
  description,
  placeholder,
  meta: { touched, error: syncError, submitError },
  currency,
  newDesign,
  ...other
}: FieldRenderProps<number | null, HTMLInputElement> &
  PureFormMoneyFieldProps) {
  const error = syncError || submitError
  const showError = (touched || submitError) && !!error

  const actualDescription =
    description && !newDesign ? <em>{description}</em> : description

  return (
    <MoneyField
      {...other}
      currency={currency}
      description={actualDescription}
      error={showError ? error : null}
      label={label}
      name={input.name}
      newDesign={newDesign}
      onChange={(target) => input.onChange(target)}
      placeholder={placeholder}
      value={input.value}
    />
  )
}

export default function FormMoneyField({
  name,
  fieldProps = EMPTY_FIELD_PROPS,
  validate,
  ...innerProps
}: Props) {
  return (
    <Field<
      number | null,
      FieldRenderProps<number | null, HTMLInputElement>,
      HTMLInputElement
    >
      format={(value) =>
        value === null || typeof value === 'undefined'
          ? ''
          : Number(value / 100).toString()
      }
      name={name}
      parse={(value: string) =>
        value === '' ? null : Math.round(parseFloat(value) * 100)
      }
      render={(props) => (
        <PureFormMoneyField {...props} {...innerProps} name={name} />
      )}
      validate={validate}
      {...fieldProps}
    />
  )
}
